import React, { useState, useEffect, useCallback } from 'react';
import { GitBranch, RefreshCw, History, Save, ArrowRight, Plus, Minus, Edit } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { apqpService } from '../../services/api';

const KIND_META = {
  changed: { color: 'bg-amber-100 text-amber-800 border-amber-300', icon: Edit,  label: 'Modificata' },
  added:   { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: Plus,  label: 'Nuova' },
  deleted: { color: 'bg-red-100 text-red-800 border-red-300', icon: Minus, label: 'Eliminata' },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT') : '—';
const fmtDt = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export default function ApqpRevisionsTab({ projectId }) {
  const [baselines, setBaselines] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revDlg, setRevDlg] = useState({ open: false, reason: '' });
  const [baseDlg, setBaseDlg] = useState({ open: false, reason: '' });
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        apqpService.listBaselines(projectId),
        apqpService.listRevisions(projectId),
      ]);
      setBaselines(b.data || []);
      setRevisions(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento revisioni');
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const currentBaseline = baselines.find(b => b.is_current);

  const saveRevision = async () => {
    if (!revDlg.reason.trim()) { toast.error('Il motivo è obbligatorio'); return; }
    try {
      const r = await apqpService.createRevision(projectId, { reason: revDlg.reason.trim() });
      toast.success(`Revisione #${r.data.revision_number} salvata · ${r.data.changes_count} modifiche`);
      setRevDlg({ open: false, reason: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio revisione');
    }
  };

  const saveBaseline = async () => {
    try {
      const r = await apqpService.createBaseline(projectId, { reason: baseDlg.reason.trim() || null });
      toast.success(`Baseline v${r.data.version} salvata`);
      setBaseDlg({ open: false, reason: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio baseline');
    }
  };

  return (
    <div className="space-y-4" data-testid="apqp-revisions-tab">
      {/* Baseline card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-slate-500" />
              <h3 className="font-semibold text-slate-800">Baseline</h3>
              {currentBaseline && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border text-[10px]">
                  v{currentBaseline.version} corrente
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button size="sm" onClick={() => setBaseDlg({ open: true, reason: '' })} data-testid="update-baseline-btn">
                <Save className="w-4 h-4 mr-1" /> Aggiorna baseline
              </Button>
            </div>
          </div>
          {loading && baselines.length === 0 ? (
            <p className="text-xs text-slate-400 mt-2">Caricamento…</p>
          ) : currentBaseline ? (
            <p className="text-xs text-slate-500 mt-2">
              La baseline v{currentBaseline.version} contiene <strong>{currentBaseline.snapshot?.length || 0}</strong> attività,
              salvata il {fmtDt(currentBaseline.created_at)} da <span className="font-mono">{currentBaseline.created_by || '—'}</span>.
              {currentBaseline.reason && <> Motivo: <em>{currentBaseline.reason}</em></>}
            </p>
          ) : (
            <p className="text-sm text-slate-400 mt-2">Nessuna baseline salvata.</p>
          )}
          {baselines.length > 1 && (
            <div className="mt-3 pt-3 border-t">
              <div className="text-xs text-slate-500 mb-1.5">Storico baseline:</div>
              <div className="flex flex-wrap gap-1.5">
                {baselines.map(b => (
                  <Badge
                    key={b.id}
                    className={`text-[10px] border ${b.is_current ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-600 border-slate-300'}`}
                  >
                    v{b.version} · {fmt(b.created_at)}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revisions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <h3 className="font-semibold text-slate-800">Revisioni della pianificazione</h3>
              <Badge className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px]">{revisions.length}</Badge>
            </div>
            <Button size="sm" onClick={() => setRevDlg({ open: true, reason: '' })} data-testid="new-revision-btn">
              <Plus className="w-4 h-4 mr-1" /> Salva revisione
            </Button>
          </div>
          {loading && revisions.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Caricamento…</p>
          ) : revisions.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center" data-testid="revisions-empty">
              Nessuna revisione registrata. Modifica le date del Gantt e poi salva una revisione con il motivo.
            </p>
          ) : (
            <ol className="relative border-l-2 border-slate-100 ml-3 space-y-3" data-testid="revisions-list">
              {revisions.map(r => {
                const key = r.id;
                const isOpen = expanded[key];
                return (
                  <li key={key} className="ml-4">
                    <div className="absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white bg-slate-400" />
                    <div className="flex items-start gap-2 flex-wrap">
                      <Badge className="bg-slate-900 text-white border border-slate-900 text-[10px]">
                        Rev #{r.revision_number}
                      </Badge>
                      <span className="text-xs text-slate-500 font-mono">{fmtDt(r.created_at)}</span>
                      <span className="text-xs text-slate-500">{r.created_by}</span>
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]">
                        {r.changes_count} modifiche
                      </Badge>
                      {r.baseline_version && (
                        <span className="text-[10px] text-slate-400">vs baseline v{r.baseline_version}</span>
                      )}
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">{r.reason}</div>
                    {r.changes_count > 0 && (
                      <button
                        className="text-xs text-blue-600 hover:underline mt-1"
                        onClick={() => setExpanded(e => ({ ...e, [key]: !e[key] }))}
                        data-testid={`rev-toggle-${r.id}`}
                      >
                        {isOpen ? 'Nascondi modifiche' : 'Mostra modifiche'}
                      </button>
                    )}
                    {isOpen && (
                      <div className="mt-2 border rounded-md p-2 bg-slate-50">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500 text-left">
                              <th className="py-1">Attività</th>
                              <th>Tipo</th>
                              <th>Prima</th>
                              <th></th>
                              <th>Dopo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.changes.map((c, i) => {
                              const meta = KIND_META[c.kind] || KIND_META.changed;
                              const Icon = meta.icon;
                              return (
                                <tr key={i} className="border-t border-slate-200">
                                  <td className="py-1 font-medium">{c.name}</td>
                                  <td>
                                    <Badge className={`border text-[10px] ${meta.color}`}>
                                      <Icon className="w-3 h-3 mr-0.5 inline" /> {meta.label}
                                    </Badge>
                                  </td>
                                  <td className="font-mono text-slate-500">
                                    {c.before ? `${c.before.planned_start || '—'} → ${c.before.planned_end || '—'}` : '—'}
                                  </td>
                                  <td className="text-slate-400"><ArrowRight className="w-3 h-3 inline" /></td>
                                  <td className={`font-mono ${c.kind === 'deleted' ? 'text-red-600' : 'text-emerald-700'}`}>
                                    {c.after ? `${c.after.planned_start || '—'} → ${c.after.planned_end || '—'}` : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* Update baseline dialog */}
      <Dialog open={baseDlg.open} onOpenChange={(o) => !o && setBaseDlg({ open: false, reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiorna baseline</DialogTitle>
            <DialogDescription>
              Salva una nuova versione della baseline con lo stato attuale delle attività. La baseline precedente viene mantenuta nello storico.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motivo (opzionale)</Label>
            <Input value={baseDlg.reason} onChange={e => setBaseDlg(d => ({ ...d, reason: e.target.value }))} data-testid="baseline-reason-input" placeholder="es. Ri-pianificazione post riesame progetto" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBaseDlg({ open: false, reason: '' })}>Annulla</Button>
            <Button onClick={saveBaseline} data-testid="save-baseline-btn">Salva baseline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New revision dialog */}
      <Dialog open={revDlg.open} onOpenChange={(o) => !o && setRevDlg({ open: false, reason: '' })}>
        <DialogContent className="max-w-md" data-testid="apqp-new-revision-dialog">
          <DialogHeader>
            <DialogTitle>Salva revisione</DialogTitle>
            <DialogDescription>
              Confronta lo stato attuale con l&apos;ultima baseline (v{currentBaseline?.version || '—'}) e registra la revisione con il motivo.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motivo *</Label>
            <Input value={revDlg.reason} onChange={e => setRevDlg(d => ({ ...d, reason: e.target.value }))} data-testid="revision-reason-input" placeholder="es. Slittamento consegna grezzi" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevDlg({ open: false, reason: '' })}>Annulla</Button>
            <Button onClick={saveRevision} data-testid="save-revision-btn">Salva revisione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
